import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CustomAxios from "../utils/CustomAxios";
import { FilterMatchMode } from "primereact/api";
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';

export function CustomDeductionTable({ onNewDeductionClick }) {
    const navigate = useNavigate();
    const [deductionRecords, setDeductionRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [viewingBatch, setViewingBatch] = useState(null);
    const [deletingBatch, setDeletingBatch] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [batchToDelete, setBatchToDelete] = useState(null);
    const [deleteError, setDeleteError] = useState('');
    const [deleteSuccess, setDeleteSuccess] = useState(false);
    const [filters, setFilters] = useState({
        narration: { value: null, matchMode: FilterMatchMode.CONTAINS },
        bank: { value: null, matchMode: FilterMatchMode.CONTAINS },
        chequeno: { value: null, matchMode: FilterMatchMode.CONTAINS },
        transdate: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });

    useEffect(() => {
        fetchDeductionRecords();
    }, []);

    const fetchDeductionRecords = async () => {
        setLoading(true);
        try {
            const response = await CustomAxios.get('oasis/custom_deduction_records/');
            setDeductionRecords(response.data.data || []);
        } catch (error) {
            setDeductionRecords([]);
        } finally {
            setLoading(false);
        }
    };

    const handleViewBatch = async (rowData) => {
        setViewingBatch(rowData);
        try {
            const response = await CustomAxios.post('oasis/custom_deduction_batch_details/', {
                narration: rowData.narration,
                bank: rowData.bank,
                chequeno: rowData.chequeno,
                transdate: rowData.transdate,
                deduction_amount: rowData.deduction_amount
            });

            const successfulDeductions = response.data.successful || [];
            const skippedDeductions = response.data.skipped || [];

            // Navigate to results page with the member details
            navigate('/custom-deduction-results', {
                state: {
                    deductionResults: successfulDeductions,
                    skippedDeductions: skippedDeductions
                }
            });
        } catch (error) {
            // Error handling - could show user notification here if needed
        } finally {
            setViewingBatch(null);
        }
    };

    const handleDeleteClick = (rowData) => {
        setBatchToDelete(rowData);
        setDeleteError('');
        setDeleteSuccess(false);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!batchToDelete) return;

        setDeletingBatch(batchToDelete);
        setDeleteError('');

        try {
            await CustomAxios.post('oasis/delete_custom_deduction_batch/', {
                narration: batchToDelete.narration,
                bank: batchToDelete.bank,
                chequeno: batchToDelete.chequeno,
                transdate: batchToDelete.transdate,
                deduction_amount: batchToDelete.deduction_amount
            });

            // Refresh the records list
            await fetchDeductionRecords();

            // Keep dialog open, show success
            setDeleteSuccess(true);
        } catch (error) {
            setDeleteError(error.response?.data?.error || 'Failed to delete batch. Please try again.');
        } finally {
            setDeletingBatch(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setBatchToDelete(null);
        setDeleteError('');
        setDeleteSuccess(false);
    };

    const dateFormat = (rowData) => {
        if (!rowData.transdate) return '';
        const date = new Date(rowData.transdate);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const amountFormat = (rowData) => {
        if (!rowData.deduction_amount) return '0.00';
        return parseFloat(rowData.deduction_amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const [customDeductionColumns, setCustomDeductionColumns] = useState([
        { field: "narration", header: "Narration", visible: true, style: { width: "200px", maxWidth: "200px" }, filter: true },
        { field: "bank", header: "Bank", visible: true, style: { width: "120px", maxWidth: "120px" }, filter: true },
        { field: "chequeno", header: "Cheque No", visible: true, style: { width: "120px", maxWidth: "120px" }, filter: true },
        { field: "deduction_amount", header: "Deduction Amount", body: amountFormat, visible: true, style: { width: "150px", maxWidth: "150px" } },
        { field: "transdate", header: "Trans Date", body: dateFormat, visible: true, style: { width: "120px", maxWidth: "120px" }, filter: true },
        { field: "transtype", header: "Deduct From", visible: true, style: { width: "120px", maxWidth: "120px" } },
        { field: "member_count", header: "Member Count", visible: true, style: { width: "130px", maxWidth: "130px" } }
    ]);

    const header = (
        <div className="flex align-items-center justify-content-end gap-2" style={{ display: "flex", justifyContent: "flex-end" }}>
            <Typography sx={{ fontFamily: "inter", fontSize: "16px", fontWeight: "600", lineHeight: "28px", color: "#242424" }}>Download Custom Deductions</Typography>
            <Button sx={{ display: "flex" }} type="button" icon="pi pi-file-excel" severity="success" rounded data-pr-tooltip="XLS" ><DownloadIcon sx={{ color: "#64748B", fontSize: "24px" }} /></Button>
        </div>
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", width: "100%" }}>
            {loading ? (
                <Box sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "200px"
                }}>
                    <CircularProgress sx={{ color: "#288d2c" }} />
                </Box>
            ) : (
                <Box sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                    <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
                        <DataTable
                            value={deductionRecords}
                            visible={deductionRecords?.length > 0 && true}
                            header={deductionRecords?.length > 0 ? header : null}
                            scrollable
                            scrollHeight="flex"
                            style={{ backgroundColor: "#F1F5F9", width: "100%" }}
                            rows={10}
                            dataKey="narration"
                            paginator
                            removableSort
                            resizableColumns
                            filters={filters}
                            filterDisplay="row"
                            emptyMessage="No custom deduction records found."
                        >
                            <Column header="SN" headerStyle={{ width: '50px' }} body={(data, options) => options.rowIndex + 1}></Column>
                            {customDeductionColumns.filter(col => col.visible).map((col, ind) => (
                                <Column
                                    className="custom-column-style"
                                    style={col.style}
                                    key={ind}
                                    field={col.field}
                                    filterElement={col.filterElement}
                                    header={col.header}
                                    sortable={col.sortable}
                                    body={col.body}
                                    filter={col.filter}
                                    alignFrozen="left"
                                    dataType={col.datatype}
                                    filterField={col.filterField}
                                    filterPlaceholder={col.filterPlaceholder}
                                    frozen={col.frozen}
                                />
                            ))}
                            <Column
                                header="Action"
                                headerStyle={{ width: '150px' }}
                                bodyStyle={{ width: '150px', textAlign: 'center' }}
                                body={(rowData) => (
                                    <Box sx={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={viewingBatch?.narration === rowData.narration && viewingBatch?.transdate === rowData.transdate ? (
                                                <CircularProgress size={16} sx={{ color: "#288d2c" }} />
                                            ) : (
                                                <VisibilityIcon />
                                            )}
                                            onClick={() => handleViewBatch(rowData)}
                                            disabled={viewingBatch?.narration === rowData.narration && viewingBatch?.transdate === rowData.transdate || deletingBatch?.narration === rowData.narration && deletingBatch?.transdate === rowData.transdate}
                                            sx={{
                                                textTransform: "none",
                                                fontFamily: "inter",
                                                fontSize: "12px",
                                                color: "#288d2c",
                                                borderColor: "#288d2c",
                                                "&:hover": {
                                                    borderColor: "#1f6d22",
                                                    backgroundColor: "#E8F5E9"
                                                }
                                            }}
                                        >
                                            View
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={deletingBatch?.narration === rowData.narration && deletingBatch?.transdate === rowData.transdate ? (
                                                <CircularProgress size={16} sx={{ color: "#d32f2f" }} />
                                            ) : (
                                                <DeleteIcon />
                                            )}
                                            onClick={() => handleDeleteClick(rowData)}
                                            disabled={viewingBatch?.narration === rowData.narration && viewingBatch?.transdate === rowData.transdate || deletingBatch?.narration === rowData.narration && deletingBatch?.transdate === rowData.transdate}
                                            sx={{
                                                textTransform: "none",
                                                fontFamily: "inter",
                                                fontSize: "12px",
                                                color: "#d32f2f",
                                                borderColor: "#d32f2f",
                                                "&:hover": {
                                                    borderColor: "#b71c1c",
                                                    backgroundColor: "#ffebee"
                                                }
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    </Box>
                                )}
                            />
                        </DataTable>
                    </Box>
                </Box>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={(event, reason) => {
                    if (reason === 'backdropClick') return;
                    handleDeleteCancel();
                }}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontFamily: "inter", fontSize: "18px", fontWeight: "600" }}>
                    Delete Custom Deduction Batch
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontFamily: "inter", fontSize: "14px", mb: 2 }}>
                        Are you sure you want to delete this deduction batch? This action cannot be undone.
                    </DialogContentText>
                    {batchToDelete && (
                        <Box sx={{ mt: 2, p: 2, backgroundColor: "#F1F5F9", borderRadius: "4px" }}>
                            <Typography sx={{ fontFamily: "inter", fontSize: "14px", fontWeight: "600", mb: 1 }}>
                                Batch Details:
                            </Typography>
                            <Typography sx={{ fontFamily: "inter", fontSize: "13px", mb: 0.5 }}>
                                <strong>Narration:</strong> {batchToDelete.narration}
                            </Typography>
                            <Typography sx={{ fontFamily: "inter", fontSize: "13px", mb: 0.5 }}>
                                <strong>Bank:</strong> {batchToDelete.bank}
                            </Typography>
                            <Typography sx={{ fontFamily: "inter", fontSize: "13px", mb: 0.5 }}>
                                <strong>Cheque No:</strong> {batchToDelete.chequeno}
                            </Typography>
                            <Typography sx={{ fontFamily: "inter", fontSize: "13px", mb: 0.5 }}>
                                <strong>Trans Date:</strong> {batchToDelete.transdate}
                            </Typography>
                            <Typography sx={{ fontFamily: "inter", fontSize: "13px", mb: 0.5 }}>
                                <strong>Member Count:</strong> {batchToDelete.member_count}
                            </Typography>
                            <Typography sx={{ fontFamily: "inter", fontSize: "13px" }}>
                                <strong>Deduction Amount:</strong> {amountFormat(batchToDelete)}
                            </Typography>
                        </Box>
                    )}
                    {deleteError && (
                        <Typography sx={{ fontFamily: "inter", fontSize: "14px", color: "#d32f2f", mt: 2 }}>
                            {deleteError}
                        </Typography>
                    )}
                    {deleteSuccess && (
                        <Typography sx={{ fontFamily: "inter", fontSize: "14px", color: "#1f6d22", mt: 2 }}>
                            Batch deleted successfully.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={handleDeleteCancel}
                        disabled={deletingBatch !== null}
                        sx={{
                            textTransform: "none",
                            fontFamily: "inter",
                            fontSize: "14px"
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        disabled={deletingBatch !== null || deleteSuccess}
                        variant="contained"
                        color="error"
                        sx={{
                            textTransform: "none",
                            fontFamily: "inter",
                            fontSize: "14px"
                        }}
                    >
                        {deleteSuccess ? "Deleted" : deletingBatch ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

